<template>
  <div id="statistics">
    <div class="container-large px-4">
      <h2>Στατιστικά</h2>

      <div v-if="loaded">
        <!-- KPI Cards -->
        <div class="row mt-4">
          <div class="col-6 col-md-3 mb-3">
            <div class="stat-card">
              <div class="stat-label">Έσοδα (30 ημέρες)</div>
              <div class="stat-value">{{ formatCurrency(stats.revenue.last30d) }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="stat-card">
              <div class="stat-label">Έσοδα (7 ημέρες)</div>
              <div class="stat-value">{{ formatCurrency(stats.revenue.last7d) }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="stat-card">
              <div class="stat-label">Σύνολο εσόδων</div>
              <div class="stat-value">{{ formatCurrency(stats.revenue.total) }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="stat-card">
              <div class="stat-label">Πελάτες</div>
              <div class="stat-value">{{ stats.customers.total }}<span class="stat-sub">+{{ stats.customers.new30d }} νέοι</span></div>
            </div>
          </div>
        </div>

        <!-- Subscriptions & Location -->
        <div class="row mt-2">
          <div class="col-6 col-md-3 mb-3">
            <div class="stat-card stat-green">
              <div class="stat-label">Ενεργές συνδρομές</div>
              <div class="stat-value">{{ stats.subscriptions.active }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="stat-card stat-red">
              <div class="stat-label">Ακυρωμένες συνδρομές</div>
              <div class="stat-value">{{ stats.subscriptions.cancelled }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="stat-card">
              <div class="stat-label">Ιωάννινα (30 ημ.)</div>
              <div class="stat-value">{{ stats.locationSplit.ioannina }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="stat-card">
              <div class="stat-label">Εκτός Ιωαννίνων (30 ημ.)</div>
              <div class="stat-value">{{ stats.locationSplit.outdoor }}</div>
            </div>
          </div>
        </div>

        <!-- Orders by Status -->
        <div class="row mt-4">
          <div class="col-12 col-md-6 mb-4">
            <div class="stat-section">
              <h5>Παραγγελίες ανά κατάσταση</h5>
              <table class="table table-sm table-bordered mt-3">
                <tbody>
                  <tr v-for="item in stats.ordersByStatus" :key="item.status">
                    <td>
                      <span class="font-weight-bold" :style="`color:${statusColors[item.status] || '#333'}`">
                        {{ $store.getters.getStatus(item.status) || item.status }}
                      </span>
                    </td>
                    <td class="text-right font-weight-bold">{{ item.count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Top Products -->
          <div class="col-12 col-md-6 mb-4">
            <div class="stat-section">
              <h5>Δημοφιλή προϊόντα (30 ημέρες)</h5>
              <table class="table table-sm table-bordered mt-3">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Προϊόν</th>
                    <th class="text-right">Ποσότητα</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(product, index) in stats.topProducts" :key="index">
                    <td>{{ index + 1 }}</td>
                    <td>{{ product.name }}</td>
                    <td class="text-right font-weight-bold">{{ product.totalQuantity }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Orders per Day Chart -->
        <div class="row mt-2">
          <div class="col-12 mb-4">
            <div class="stat-section">
              <h5>Παραγγελίες & Έσοδα ανά ημέρα (30 ημέρες)</h5>
              <table class="table table-sm table-striped table-bordered mt-3" style="max-height:400px;overflow-y:auto;">
                <thead>
                  <tr>
                    <th>Ημερομηνία</th>
                    <th class="text-right">Παραγγελίες</th>
                    <th class="text-right">Έσοδα</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="day in stats.ordersPerDay" :key="day.date">
                    <td>{{ day.date }}</td>
                    <td class="text-right">{{ day.count }}</td>
                    <td class="text-right font-weight-bold">{{ formatCurrency(day.revenue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-5">
        <p>Φόρτωση στατιστικών...</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stat-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.2rem;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.stat-card.stat-green { border-left: 4px solid #16a34a; }
.stat-card.stat-red { border-left: 4px solid #dc2626; }
.stat-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}
.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
}
.stat-sub {
  font-size: 12px;
  font-weight: 400;
  color: #16a34a;
  margin-left: 6px;
}
.stat-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.2rem;
  h5 { font-size: 15px; font-weight: 700; }
}
</style>

<script>
import { mapState } from 'vuex';
export default {
  data() {
    return {
      loaded: false,
      stats: null,
    }
  },
  computed: {
    ...mapState({
      statusColors: state => state.status_colors
    })
  },
  mounted() {
    this.fetchStats();
  },
  methods: {
    fetchStats() {
      this.axios.get('/admin/orders/statistics').then(res => {
        this.stats = res.data;
        this.loaded = true;
      }).catch(() => {
        this.$notify({ type: 'error', title: 'Σφάλμα', text: 'Δεν ήταν δυνατή η φόρτωση των στατιστικών.' });
      });
    },
    formatCurrency(val) {
      return parseFloat(val || 0).toFixed(2) + '€';
    }
  }
}
</script>
